import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import CloseBtnIcon from "../Assets/svgs/CloseBtnIcon";
import CloseIcon from "../Assets/svgs/CloseIcon";
import ExcelIcon from "../Assets/svgs/ExcelIcon";
import { nameWithNumberValidation } from "../Common/Validations";
import axiosInstance from "../Service/Interceptor";
import { ErrorModel } from "./ErrorModel";
import { SuccessModel } from "./SuccessModel";
import { PreviewImportError } from "./PreviewImportErrorModal";
import { debounce } from "../Common/debounce";
import MultiSelectDropdown from "../Common/MultiSelectDropdown";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import { getAssistantList } from "../Redux/Slice/Assistant/AssistantsListSlice";
import { EditableCSVContactDataModal } from "./EditableCSVContactDataModal";
import EyeIcon from "../Assets/svgs/EyeIcon";
import { uuidGenerator } from "../Common/utils";
import { DefaultCSVColumns } from "../Common/Constants";

const UploadContactUsersForGroupModal = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [groupName, setGroupName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const isCsvFile = selectedFile ? selectedFile?.type == "text/csv" : false;
  const [uploadController, setUploadController] = useState(null);
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [csvErrorModal, setCsvErrorModal] = useState({
    visible: false,
    errorData: null,
  });
  const [editCSVModal, setEditCSVModal] = useState(false);
  const [uploadingData, setUploadingData] = useState({
    isUploading: false,
    percentage: 0,
  });
  const [formError, setFormError] = useState({
    groupNameError: "",
  });
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({
    visible: false,
    message: "",
  });
  const assistantList = useSelector(
    (state) => state?.assistants?.assistantsList
  );
  const [excelData, setExcelData] = useState({
    columns: [],
    data: [],
  });

  const allowedExtensions = [
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const maxSize = 10 * 1024 * 1024; // 10 MB size limit per file

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({
        visible: false,
        message: "",
      });
      if (typeof callback == "function") {
        callback();
      }
    }, 2000);
  };

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message,
    });

    setTimeout(() => {
      setSuccessModal({
        visible: false,
        message: "",
      });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const formattedAssistantList = useMemo(() => {
    let _formattedDataList = Array.isArray(assistantList?.data)
      ? assistantList?.data?.map((assistant) => {
          return { value: assistant?.id, label: assistant?.name };
        })
      : [];
    return _formattedDataList;
  }, [assistantList, assistantList?.data]);

  const handleChangeAssistantListData = (selectedList) => {
    const selectedListIds = Array.isArray(selectedList)
      ? selectedList.map((item) => item?.value)
      : [];
    setSelectedAssistants(selectedListIds);
  };

  const handleChangeGroupName = (e) => {
    let { value } = e?.target;
    value = value.trimStart();
    const isValid = nameWithNumberValidation(value);
    if (isValid || value.trim() === "") {
      setFormError({ ...formError, groupNameError: "" });
    } else {
      setFormError({
        ...formError,
        groupNameError: "Please enter valid group name",
      });
    }
    let capitalized = value.charAt(0).toString().toUpperCase();
    value = capitalized + value.substring(1);
    setGroupName(value);
  };

  const uploadUsers = async () => {
    // const formData = new FormData();
    // formData.append("name", groupName);
    // formData.append("assistantIds", selectedAssistants);
    // formData.append("file", selectedFile);
    // formData.append("columns", excelData.columns);
    // formData.append("data", excelData.data);
    const formData = {
      name: groupName,
      assistantIds: selectedAssistants.join(","),
      columns: excelData.columns,
      data: excelData.data?.map((_dataItem) => ({
        ..._dataItem,
        id: undefined,
        isNewAdded: undefined,
      })),
    };

    const newController = new AbortController(); // Create new AbortController per upload
    setUploadController(newController); // Set controller to state

    setUploadingData({
      isUploading: true,
      percentage: 0,
    });
    try {
      const response = await axiosInstance.post(
        // "customer/contacts/group/import",
        "customer/contacts/group/import/test",
        formData,
        {
          signal: newController.signal,
          onUploadProgress: (progressEvent) => {
            const { loaded, total } = progressEvent;
            const percentage = Math.round((loaded * 100) / total);
            setUploadingData({
              isUploading: true,
              percentage: percentage,
            });
          },
        }
      );

      setUploadingData({
        isUploading: false,
        percentage: 0,
      });
      if (response.data?.success === true) {
        setSelectedFile(null);
        showSuccessMessage("Contacts imported successfully!", onSuccess);
      } else {
        if (
          Array.isArray(response?.data?.data) &&
          response?.data?.data?.length
        ) {
          setCsvErrorModal({
            visible: true,
            errorData: response?.data?.data,
          });
        } else {
          showErrorMessage(response.data?.error?.message);
        }
      }
    } catch (error) {
      setUploadingData({
        isUploading: false,
        percentage: 0,
      });
      if (axios.isCancel(error)) {
        showErrorMessage("The contacts upload was successfully canceled!");
      } else {
        showErrorMessage(error?.message?.error?.message);
      }
    }
  };

  const debouncedUploadUsers = debounce(uploadUsers, 800);

  const parseCsvFile = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const binaryStr = e.target.result;
        const workbook = XLSX.read(binaryStr, {
          type: "binary",
          cellDates: true,
        });

        // Get the first sheet and convert to JSON
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const allRows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: true,
        });

        // Separate the first row as an array
        const header = allRows[0];
        let dateFields = [];
        // Convert the rest of the row into JSON using the first row as keys
        const rows = allRows.slice(1).map((row) => {
          const rowData = {};
          header.forEach((key, i) => {
            rowData[key] = row[i] || null; // Ensure null for missing values
            if (row[i] instanceof Date && !dateFields.includes(key)) {
              dateFields.push(key);
            }
          });
          return rowData;
        });
        const excelColumns = Array.isArray(header)
          ? header?.map((column) => ({
              id: column,
              label: column,
              accessor: column,
              minWidth: 100,
              dataType: dateFields.includes(column)
                ? "date"
                : column == "email"
                ? "email"
                : column == "phoneNumber"
                ? "phoneNo"
                : column == "countryCallingCode"
                ? "countryCode"
                : "text",
              isDisabled: DefaultCSVColumns?.some(
                (defaultColumn) => defaultColumn == column
              ),
              options: [],
            }))
          : [];
        const excelRows = Array.isArray(rows)
          ? rows
              ?.filter((objRow) => {
                let isHasValue = false;
                Object.keys(objRow).forEach((key) => {
                  if (objRow[key] != null) {
                    isHasValue = true;
                  }
                });
                return isHasValue;
              })
              ?.map((header) => ({
                id: uuidGenerator(),
                ...header,
              }))
          : [];

        setExcelData({
          columns: excelColumns,
          data: excelRows,
        });
      };

      reader.onerror = (error) => {
        console.error(`Error reading file: ${error?.message}`);
        setExcelData({
          columns: [],
          data: [],
        });
      };

      reader.readAsArrayBuffer(file);
    }
  };

  const validateFiles = (files) => {
    for (let file of files) {
      const fileExtension = file?.type;

      // Check if file extension is allowed
      if (!allowedExtensions.includes(fileExtension)) {
        showErrorMessage(`Invalid file type: ${file.name}!`);
        return false;
      }

      // Check if file size is within limit
      if (file.size > maxSize) {
        showErrorMessage(`File size too large: ${file.name}!`);
        return false;
      }
    }

    return true;
  };

  const onPickFile = (e) => {
    const files = e?.target?.files;
    if (!files.length) return;
    const isValid = validateFiles(files);
    if (isValid) {
      setSelectedFile(files[0]);
      parseCsvFile(files[0]);
    }
    setTimeout(() => {
      e.target.value = "";
    }, 300);
  };

  const cancelUpload = () => {
    if (uploadController) {
      uploadController?.abort(); // Cancel the ongoing upload
      setUploadController(null); // Reset controller in state
    }
  };

  const onCloseImportError = () => {
    setCsvErrorModal({
      visible: false,
      errorData: null,
    });
  };

  const onClickTryAgain = () => {
    // setSelectedFile(null);
    onCloseImportError();
  };

  const FetchAssistantsList = async () => {
    await dispatch(getAssistantList({ text: "", isDropdown: true }));
  };

  useEffect(() => {
    FetchAssistantsList();
  }, []);

  return successModal.visible ? (
    <SuccessModel message={successModal.message} />
  ) : errorModal.visible ? (
    <ErrorModel message={errorModal.message} />
  ) : csvErrorModal?.visible ? (
    <PreviewImportError
      fileName={selectedFile?.name}
      errorsData={csvErrorModal?.errorData}
      onClose={onCloseImportError}
      onPressTryAgain={onClickTryAgain}
    />
  ) : editCSVModal ? (
    <EditableCSVContactDataModal
      fileName={selectedFile?.name}
      isCsvFile={isCsvFile}
      excelData={excelData}
      onSave={(data) => {
        // setEditCSVModal(false);
        setExcelData(data);
      }}
      onClose={() => {
        setEditCSVModal(false);
      }}
    />
  ) : (
    <div className="modal modal--createAssistant modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--md">
          <div className="modal__header">
            <h1 className="modal__header--title">Upload contacts</h1>
            <p className="para">Please upload CSV file to import contacts</p>
            <button className="btn btn__transparent btnClose" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="modal__body  modal-body-left">
            <div className="scheduleCall">
              <div className="scheduleCall__row">
                <div className="form__Field form__Field--full">
                  <label for="groupName" className="form__label">
                    Group Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="groupName"
                    className="form__input form__input--sm"
                    placeholder="Group Name"
                    value={groupName}
                    maxLength={30}
                    onChange={handleChangeGroupName}
                  />
                  {formError.groupNameError ? (
                    <div className="form__error">
                      {formError.groupNameError}
                    </div>
                  ) : null}
                </div>
                <div className="formCol">
                  <div className="form__Field form__Field--full">
                    <label for="firstMessage" className="form__label">
                      Assistant<sup className="form__label__asterisk">*</sup>
                    </label>
                    <MultiSelectDropdown
                      id="assistant"
                      name="assistantId"
                      options={formattedAssistantList}
                      selectedValue={selectedAssistants}
                      onChange={handleChangeAssistantListData}
                    />
                  </div>
                </div>
                <div className="form__Field form__Field--full">
                  <label for="UploadDocument" className="form__label">
                    Upload Document <span style={{ color: "red" }}>*</span>
                  </label>
                  <div className="form__browsefileBox">
                    <div className="form__browsefile form__browsefile--sm">
                      {selectedFile ? (
                        <div className="form__browsefile__value">
                          <ExcelIcon />
                          <div className="form__browsefile__value__detail">
                            <p className="para">{selectedFile?.name}</p>
                            {uploadingData?.isUploading ? (
                              <div className="progressBox">
                                <div className="progressbar small">
                                  <div
                                    className="progress"
                                    style={{
                                      width: `${uploadingData.percentage}%`,
                                    }}
                                  ></div>
                                </div>
                                <span className="progressValue">
                                  {uploadingData.percentage}%
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                      <input
                        type="file"
                        id="UploadFile"
                        className="form__uploadfiled"
                        accept=".csv,.xlsx"
                        onChange={onPickFile}
                      />
                      {!selectedFile ? (
                        <>
                          <label className="btn btn__primary" for="UploadFile">
                            Browse File
                          </label>
                          <input
                            type="text"
                            className="form__input"
                            placeholder="No file chosen"
                            disabled
                          />
                        </>
                      ) : null}
                      {selectedFile ? (
                        <span
                          className="form__browsefile__closeBtn"
                          onClick={() =>
                            uploadingData.isUploading
                              ? cancelUpload()
                              : setSelectedFile(null)
                          }
                        >
                          <CloseBtnIcon />
                        </span>
                      ) : null}
                    </div>
                    {selectedFile && selectedFile?.name ? (
                      <button
                        onClick={() => setEditCSVModal(true)}
                        disabled={uploadingData.isUploading}
                        className="btn btn__primary btn--preview"
                      >
                        <EyeIcon />
                        Preview
                      </button>
                    ) : null}
                  </div>
                  <p className="form__infoText">
                    <strong>Supported file type:</strong> csv, xlsc &nbsp;
                    <span>
                      <strong>Max file size:</strong> 10mb
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={onClose}>
                Cancel
              </button>
              <button
                disabled={
                  !selectedAssistants?.length ||
                  !groupName ||
                  !selectedFile ||
                  formError.groupNameError ||
                  uploadingData.isUploading
                }
                className="btn btn__primary"
                onClick={debouncedUploadUsers}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default UploadContactUsersForGroupModal;
